export interface FormantAnalysis {
  fundamentalF0: number;
  formantF1: number;
  formantF2: number;
  vowelEstimate: 'a' | 'i' | 'u' | 'e' | 'o' | 'unknown';
  spectralCentroid: number;
}

/**
 * Computes Spectral Centroid and Formant Peaks using Discrete Fourier Transform (DFT)
 * to evaluate Sanskrit vowel phoneme resonance.
 */
export function analyzeFormants(
  pcmBuffer: Float32Array,
  sampleRate: number,
  fundamentalF0: number
): FormantAnalysis {
  if (!pcmBuffer || pcmBuffer.length === 0) {
    return {
      fundamentalF0,
      formantF1: 0,
      formantF2: 0,
      vowelEstimate: 'unknown',
      spectralCentroid: 0,
    };
  }

  const fftSize = pcmBuffer.length;
  const magnitudes = new Float32Array(Math.floor(fftSize / 2));

  let totalMagnitude = 0;
  let weightedFrequencySum = 0;

  // Compute Magnitude Spectrum & Spectral Centroid
  for (let k = 0; k < Math.floor(fftSize / 2); k++) {
    let real = 0;
    let imag = 0;
    for (let n = 0; n < fftSize; n++) {
      const angle = (2 * Math.PI * k * n) / fftSize;
      real += pcmBuffer[n] * Math.cos(angle);
      imag -= pcmBuffer[n] * Math.sin(angle);
    }
    const mag = Math.sqrt(real * real + imag * imag);
    magnitudes[k] = mag;

    const binFreq = (k * sampleRate) / fftSize;
    totalMagnitude += mag;
    weightedFrequencySum += binFreq * mag;
  }

  const spectralCentroid = totalMagnitude > 0 ? weightedFrequencySum / totalMagnitude : 0;

  // Extract Formant Peaks (F1: 200Hz - 1000Hz, F2: 1000Hz - 3000Hz)
  let f1 = 0;
  let f1MaxMag = 0;
  let f2 = 0;
  let f2MaxMag = 0;

  for (let k = 1; k < magnitudes.length - 1; k++) {
    const binFreq = (k * sampleRate) / fftSize;
    const mag = magnitudes[k];

    // Local peak detection
    if (mag > magnitudes[k - 1] && mag > magnitudes[k + 1]) {
      if (binFreq >= 200 && binFreq <= 1000 && mag > f1MaxMag) {
        f1MaxMag = mag;
        f1 = binFreq;
      } else if (binFreq > 1000 && binFreq <= 3000 && mag > f2MaxMag) {
        f2MaxMag = mag;
        f2 = binFreq;
      }
    }
  }

  // Map F1/F2 space to standard Sanskrit Vowel Formants
  let vowelEstimate: FormantAnalysis['vowelEstimate'] = 'unknown';
  if (f1 >= 600 && f2 >= 1100 && f2 <= 1600) {
    vowelEstimate = 'a'; // Short / Long A ( आकार )
  } else if (f1 <= 400 && f2 >= 2000) {
    vowelEstimate = 'i'; // Short / Long I ( इकार )
  } else if (f1 <= 400 && f2 <= 1100) {
    vowelEstimate = 'u'; // Short / Long U ( उकार )
  } else if (f1 > 400 && f1 < 600 && f2 >= 1700) {
    vowelEstimate = 'e'; // E ( एकार )
  } else if (f1 > 400 && f1 < 600 && f2 <= 1300) {
    vowelEstimate = 'o'; // O ( ओकार )
  }

  return {
    fundamentalF0,
    formantF1: f1,
    formantF2: f2,
    vowelEstimate,
    spectralCentroid,
  };
}
